import { useEffect, useState } from "react";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Trophy, Gamepad2 } from "lucide-react";
import { db, functions } from "../firebase-config";
import MoveIcon from "./MoveIcon";
import AvatarRing from "./AvatarRing";
import SubtitleBadge from "./SubtitleBadge";
import ShopItemPreview from "./ShopItemPreview";
import { ordinal } from "../ordinal";
import { assignRanks } from "../rank";
import { getEquippedItem } from "../cosmetics";
import { TRUST_COLOR, TEXT_DARK, TEXT_MUTED, BORDER, BETRAY_COLOR, BETRAY_BG } from "../theme";

const SECTION_LABELS = {
  subtitle: "subtitles",
  border: "avatar borders",
  bubble: "chat bubble themes",
};

function Profile({ user, userProfile, shopItems = [], onBack }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthRank, setMonthRank] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [equipError, setEquipError] = useState("");
  const [openType, setOpenType] = useState(null);

  useEffect(() => {
    loadMonthRank();
  }, [selectedMonth, selectedYear]);

  const loadMonthRank = async () => {
    try {
      const usersQuery = query(collection(db, "users"), orderBy("totalScore", "desc"), limit(100));
      const snapshot = await getDocs(usersQuery);
      const monthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

      const scored = snapshot.docs
        .map((d) => {
          const history = d.data().gameHistory || {};
          let score = 0;
          let games = 0;
          Object.entries(history).forEach(([dateKey, g]) => {
            if (dateKey.startsWith(monthKey)) {
              score += g.myScore || 0;
              games++;
            }
          });
          return { uid: d.id, score, games };
        })
        .filter((u) => u.games > 0)
        .sort((a, b) => b.score - a.score);

      const ranked = assignRanks(scored, "score");
      const mine = ranked.find((u) => u.uid === user.uid);
      setMonthRank(mine ? { rank: mine.rank, total: scored.length, score: mine.score } : null);
    } catch (error) {
      console.error("Error loading month rank:", error);
      setMonthRank(null);
    }
  };

  const getGameHistory = () => {
    return userProfile.gameHistory || {};
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getDateKey = (day, month, year) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const gameHistory = getGameHistory();
    const today = new Date();
    const isCurrentMonth = selectedMonth === today.getMonth() && selectedYear === today.getFullYear();

    const days = [];
    const weekDays = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

    weekDays.forEach(day => {
      days.push(
        <div key={`header-${day}`} style={{ textAlign: "center", fontWeight: "600", padding: "6px", fontSize: "0.6875rem", color: TEXT_MUTED }}>
          {day}
        </div>
      );
    });

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: "8px" }}></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = getDateKey(day, selectedMonth, selectedYear);
      const gameData = gameHistory[dateKey];
      const isToday = isCurrentMonth && day === today.getDate();
      const isFuture = new Date(selectedYear, selectedMonth, day) > today;

      days.push(
        <div
          key={day}
          title={gameData ? `+${gameData.myScore}` : ""}
          style={{
            padding: "8px 6px",
            minHeight: "72px",
            background: "#fff",
            border: isToday ? `2px solid ${TRUST_COLOR}` : `1px solid ${BORDER}`,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            opacity: isFuture ? 0.4 : 1,
          }}
        >
          <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED }}>
            {day}
          </div>
          {gameData && (
            <>
              <div style={{ display: "flex", gap: "3px", marginTop: "6px" }}>
                <MoveIcon move={gameData.myMove} size={12} />
                <MoveIcon move={gameData.opponentMove} size={12} flip={gameData.opponentMove === "defect"} />
              </div>
              <div style={{ fontSize: "0.625rem", fontWeight: "700", color: TEXT_DARK, marginTop: "6px" }}>
                {gameData.myScore > 0 ? "+" : ""}{gameData.myScore}
              </div>
            </>
          )}
        </div>
      );
    }

    return days;
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const monthNames = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

  const calculateMonthStats = () => {
    const gameHistory = getGameHistory();
    let gamesPlayed = 0;
    let bothTrusted = 0;
    let youBetrayed = 0;
    let theyBetrayed = 0;
    let bothBetrayed = 0;

    Object.entries(gameHistory).forEach(([dateKey, gameData]) => {
      const [keyYear, keyMonth] = dateKey.split('-').map(Number);
      if (keyMonth - 1 === selectedMonth && keyYear === selectedYear) {
        gamesPlayed++;

        if (gameData.myMove === "cooperate" && gameData.opponentMove === "cooperate") {
          bothTrusted++;
        } else if (gameData.myMove === "defect" && gameData.opponentMove === "defect") {
          bothBetrayed++;
        } else if (gameData.myMove === "defect" && gameData.opponentMove === "cooperate") {
          youBetrayed++;
        } else {
          theyBetrayed++;
        }
      }
    });

    return { gamesPlayed, bothTrusted, youBetrayed, theyBetrayed, bothBetrayed };
  };

  const stats = calculateMonthStats();

  // Every item ever bought, regardless of whether it's still active in the
  // shop — that's the whole point of a "history": the shop catalogue rotates
  // monthly, but what you already own keeps working and stays equippable.
  const purchasedAt = userProfile.purchasedAt || {};
  const ownedItems = (userProfile.ownedItems || [])
    .map((id) => shopItems.find((it) => it.id === id))
    .filter(Boolean)
    .sort((a, b) => (purchasedAt[b.id]?.toMillis() || 0) - (purchasedAt[a.id]?.toMillis() || 0));
  const equipped = userProfile.equipped || {};

  const equip = async (type, itemId) => {
    setEquipError("");
    setBusyId(itemId || `unequip-${type}`);
    try {
      await httpsCallable(functions, "equipItem")({ type, itemId });
    } catch (e) {
      setEquipError(e.message || "failed to equip");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: "560px", margin: "40px auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <AvatarRing photoURL={userProfile.photoURL} size={48} item={getEquippedItem(userProfile, shopItems, "border")} />
          <div>
            <div style={{ fontWeight: "700", fontSize: "0.9375rem", display: "flex", alignItems: "center", gap: "6px" }}>
              {userProfile.displayName}
              <SubtitleBadge item={getEquippedItem(userProfile, shopItems, "subtitle")} />
            </div>
            <div style={{ display: "flex", gap: "10px", fontSize: "0.6875rem", color: TEXT_MUTED, marginTop: "2px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Trophy size={12} /> {userProfile.totalScore || 0} pts</span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Gamepad2 size={12} /> {userProfile.gamesPlayed || 0} games</span>
            </div>
          </div>
        </div>
        <button onClick={onBack} style={{ padding: "6px 14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}>
          back
        </button>
      </div>

      {/* Month Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", padding: "10px 14px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
        <button onClick={() => changeMonth(-1)} style={{ background: "none", border: "none", padding: "4px", display: "flex" }}>
          <ChevronLeft size={18} color={TEXT_MUTED} />
        </button>
        <div style={{ fontSize: "0.8125rem", fontWeight: "600" }}>{monthNames[selectedMonth]} {selectedYear}</div>
        <button onClick={() => changeMonth(1)} style={{ background: "none", border: "none", padding: "4px", display: "flex" }}>
          <ChevronRight size={18} color={TEXT_MUTED} />
        </button>
      </div>

      {/* Month Rank */}
      {monthRank && (
        <div style={{ textAlign: "center", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "10px", marginBottom: "14px", fontSize: "0.8125rem", color: TEXT_MUTED }}>
          {monthNames[selectedMonth]} {selectedYear}: <strong style={{ color: TEXT_DARK }}>{ordinal(monthRank.rank)}</strong> out of {monthRank.total} players · <strong style={{ color: TEXT_DARK }}>{monthRank.score}</strong> pts
        </div>
      )}

      {/* Month Stats */}
      {stats.gamesPlayed > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: "14px" }}>
          <StatChip value={stats.bothTrusted} label="both trusted" moves={["cooperate", "cooperate"]} />
          <StatChip value={stats.youBetrayed} label="you betrayed" moves={["defect", "cooperate"]} />
          <StatChip value={stats.theyBetrayed} label="they betrayed" moves={["cooperate", "defect"]} />
          <StatChip value={stats.bothBetrayed} label="both betrayed" moves={["defect", "defect"]} />
        </div>
      )}

      {/* Calendar */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "6px",
        background: "#fafafa",
        padding: "12px",
        borderRadius: "10px",
        border: `1px solid ${BORDER}`,
        marginBottom: "20px",
      }}>
        {renderCalendar()}
      </div>

      {/* Customisation — one section per cosmetic slot. Clicking a section
          expands it to a pick-list of everything you own of that type, so
          equipping is "choose which one of these I want", not a flat list
          of every purchase mixed together. */}
      <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "8px" }}>customisation</div>

      {equipError && (
        <div style={{ padding: "10px", borderRadius: "8px", background: BETRAY_BG, color: BETRAY_COLOR, fontSize: "0.75rem", marginBottom: "10px", textAlign: "center" }}>
          {equipError}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {Object.keys(SECTION_LABELS).map((type) => {
          const itemsOfType = ownedItems.filter((it) => it.type === type);
          const equippedId = equipped[type];
          const equippedItem = itemsOfType.find((it) => it.id === equippedId);
          const isOpen = openType === type;
          return (
            <div key={type} style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", background: "#fff", overflow: "hidden" }}>
              <button
                onClick={() => setOpenType(isOpen ? null : type)}
                style={{ width: "100%", padding: "12px 14px", display: "flex", alignItems: "center", gap: "12px", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}
              >
                {equippedItem ? (
                  <ShopItemPreview item={equippedItem} photoURL={userProfile.photoURL} />
                ) : (
                  <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#f0f1f5", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.8125rem", fontWeight: "700" }}>{SECTION_LABELS[type]}</div>
                  <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>
                    {equippedItem ? equippedItem.name : itemsOfType.length === 0 ? "none owned yet" : "none equipped"}
                  </div>
                </div>
                {isOpen ? <ChevronUp size={16} color={TEXT_MUTED} /> : <ChevronDown size={16} color={TEXT_MUTED} />}
              </button>

              {isOpen && (
                <div style={{ borderTop: `1px solid ${BORDER}`, padding: "10px 14px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {itemsOfType.length === 0 ? (
                    <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, textAlign: "center", padding: "10px 0" }}>
                      you don't own any {SECTION_LABELS[type]} yet — check the shop.
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => equip(type, null)}
                        disabled={busyId === `unequip-${type}`}
                        style={{
                          display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "8px",
                          border: `1px solid ${!equippedItem ? TRUST_COLOR : BORDER}`,
                          background: !equippedItem ? "#f3f5ff" : "#fff", textAlign: "left",
                        }}
                      >
                        <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#f0f1f5", flexShrink: 0 }} />
                        <span style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED }}>none</span>
                      </button>
                      {itemsOfType.map((item) => {
                        const isEquipped = equippedId === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => equip(type, item.id)}
                            disabled={busyId === item.id || isEquipped}
                            style={{
                              display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "8px",
                              border: `1px solid ${isEquipped ? TRUST_COLOR : BORDER}`,
                              background: isEquipped ? "#f3f5ff" : "#fff", textAlign: "left",
                            }}
                          >
                            <ShopItemPreview item={item} photoURL={userProfile.photoURL} />
                            <span style={{ fontSize: "0.8125rem", fontWeight: "700", flex: 1 }}>{item.name}</span>
                            {isEquipped && <span style={{ fontSize: "0.6875rem", fontWeight: "700", color: TRUST_COLOR }}>equipped</span>}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatChip({ value, label, moves }) {
  return (
    <div style={{ textAlign: "center", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: "3px", marginBottom: "6px" }}>
        <MoveIcon move={moves[0]} size={13} />
        <MoveIcon move={moves[1]} size={13} flip={moves[1] === "defect"} />
      </div>
      <div style={{ fontSize: "1rem", fontWeight: "700" }}>{value}</div>
      <div style={{ fontSize: "0.625rem", color: TEXT_MUTED, marginTop: "2px" }}>
        {label}
      </div>
    </div>
  );
}

export default Profile;
